import { CSSProperties, ComponentType, forwardRef, memo, ReactNode, Ref } from 'react';
import {
  type IconProps,
  OutlinePencilIcon,
  SolidBriefcaseIcon,
  SolidCalendarIcon,
  SolidDocumentIcon,
  SolidMoneyPayIcon,
} from '@tempmee/react-icons';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, Column, Pressable, Row } from '~/commonComponents';
import { Colors, TColorKeys } from '~/themes/colors';

/** Icons available to InfoSection, mapped to their @tempmee/react-icons component. */
type TInfoSectionIcon =
  | 'briefcase-solid'
  | 'calendar-solid'
  | 'document-solid'
  | 'edit-pencil'
  | 'money-pay-solid';

const INFO_SECTION_ICONS: Record<TInfoSectionIcon, ComponentType<IconProps>> = {
  'briefcase-solid': SolidBriefcaseIcon,
  'calendar-solid': SolidCalendarIcon,
  'document-solid': SolidDocumentIcon,
  'edit-pencil': OutlinePencilIcon,
  'money-pay-solid': SolidMoneyPayIcon,
};

/** Resolve a theme color key (e.g. 'secondary_500') or raw CSS color to a CSS value. */
const resolveColor = (color: TColorKeys | string): string =>
  color in Colors ? Colors[color as TColorKeys] : color;

export interface IInfoSectionProps {
  /** Icon to display on the left */
  icon?: TInfoSectionIcon;
  /** Title text */
  title: string;
  /** Show required indicator */
  required?: boolean;
  /** Optional icon color */
  iconColor?: TColorKeys | string;
  /** Optional icon size */
  iconSize?: number;
  /** Optional title color */
  titleColor?: TColorKeys | string;
  /** Optional title font size */
  titleFontSize?: number;
  /** Optional right action button */
  rightAction?: ReactNode;
  /** Optional callback when right action is clicked */
  onRightActionPress?: () => void;
  /** Optional right action icon name */
  rightActionIcon?: TInfoSectionIcon;
  /** Optional right action icon color */
  rightActionIconColor?: TColorKeys | string;
  /** Optional right action icon size */
  rightActionIconSize?: number;
  /** Optional children content */
  children?: ReactNode;
  /** Optional container style */
  containerStyle?: CSSProperties;
  /** Optional data-cy for testing */
  dataCy?: string;
  /** Optional data-testid for testing */
  dataTestId?: string;
}

const Component = (
  {
    icon,
    title,
    required = false,
    iconColor = colors.secondary[500],
    iconSize = 24,
    titleColor = 'primary_500',
    titleFontSize = 18,
    rightAction,
    onRightActionPress,
    rightActionIcon,
    rightActionIconColor = colors.secondary[500],
    rightActionIconSize = 24,
    children,
    containerStyle,
    dataCy,
    dataTestId,
  }: IInfoSectionProps,
  ref: Ref<HTMLDivElement>,
) => {
  const LeftIcon = icon ? INFO_SECTION_ICONS[icon] : null;

  const renderRightAction = () => {
    if (rightAction) {
      return rightAction;
    }

    const RightIcon = rightActionIcon ? INFO_SECTION_ICONS[rightActionIcon] : null;
    if (RightIcon && onRightActionPress) {
      return (
        <Pressable onPress={onRightActionPress}>
          <RightIcon color={resolveColor(rightActionIconColor)} size={rightActionIconSize} />
        </Pressable>
      );
    }

    return null;
  };

  return (
    <Box
      ref={ref}
      paddingTop={24}
      paddingBottom={26}
      paddingLeft={66}
      paddingRight={66}
      border={`1.5px solid ${colors.neutral[100]}`}
      borderRadius={8}
      data-cy={dataCy}
      data-testid={dataTestId}
      style={containerStyle}
    >
      <Row alignItems="center" justifyContent="space-between" gap={16}>
        <Row alignItems="center" gap={8}>
          {LeftIcon && <LeftIcon color={resolveColor(iconColor)} size={iconSize} />}
          <Typography as="span" color={resolveColor(titleColor)} bold style={{ fontSize: titleFontSize }}>
            {title}
            {required && (
              <Typography as="span" color={colors.neutral[500]}>
                {' (Required)'}
                <Typography as="span" color={colors.error[500]}>*</Typography>
              </Typography>
            )}
          </Typography>
        </Row>
        {renderRightAction()}
      </Row>
      {children && (
        <Column marginLeft={icon ? (iconSize ?? 0) + 9 : 0} marginTop={20} marginRight={28}>
          {children}
        </Column>
      )}
    </Box>
  );
};

export const InfoSection = memo(forwardRef(Component));
